import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { Box, Drawer, IconButton, Tooltip } from "@mui/material";
import { DownIcon, HumburgerIcon, RupinLogo } from "../../../assets/icons/icon";
import { ParagraphText } from "../../../Libraries";
import romn from "../../../languageConfig/local/romn";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import snd from "../../../languageConfig/local/snd";
import eng from "../../../languageConfig/local/en";
import ur from "../../../languageConfig/local/ur";
import {
  getLangFromAsyncStorage,
  getValidStoredLanguage,
  setLangInAsyncStorage,
} from "../../../constants";
import { LanguageContext } from "../../../context/LanguageContext";
import { ThemeContext } from "../../../context/ThemeContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../context/AuthContext";

import { useSnackbar } from "../../../context/SnackbarContext";
import Cookies from "js-cookie";
import styles from "./styles.module.css";
import { getMenuBottomList, getMenuList } from "./MenuList";
import { removeAllSpaces } from "../../../utils";
import ChangeLanguageModal from "../Modals/ChangeLanguageModal";
import AppearanceModal from "../Modals/AppearanceModal";
import ConfirmationModal from "../Modals/ConfirmationModal";
import { useWalletContext } from "../../../context/WalletContext";
import { hasUnsyncedChanges } from "@nozbe/watermelondb/sync";
import { database } from "../../../database";
import { handleApi } from "../../../apis";
import { ACCOUNT_PIN_RESET } from "../../../apis/configuration";
import BusinessLoader from "../BusinessLoader";

const DEFAULT_OPEN_WIDTH = 254;
const DEFAULT_CLOSED_WIDTH = 72;
const DEFAULT_HEADER_OFFSET = 0;

const AppSideNav = ({
  isPinned = false,
  isHovered = false,
  onHoverChange,
  onMenuToggle,
  headerOffset = DEFAULT_HEADER_OFFSET,
  disableHoverClose = false,
}) => {
  const lan = getValidStoredLanguage();
  const [printerProcess, setPrinterProcess] = useState(false);
  const [I18n, changeLanguage] = useContext(LanguageContext);
  const [theme, changeTheme] = useContext(ThemeContext);
  const [language, setLanguage] = useState(lan || "en");
  const [languageModal, setLanguageModal] = useState(false);
  const [appearenceModal, setAppearenceModal] = useState(false);
  const [backupNow, setIsBackup] = useState(false);
  const [toggle, setToggle] = useState(false);
  const [currentSelection, setCurrentSelection] = useState("");
  const [bestMatchingRoute, setBestMatchingRoute] = useState("");
  const { showSnackbar } = useSnackbar();
  const [expandedMenu, setExpandedMenu] = useState("Sales");

  const [isLogout, setLogout] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [isLogoutLoading, setIsLogoutLoading] = useState(false);
  const [isHomeLoading, setIsHomeLoading] = useState(false);

  const { handleCheckEMIStatus, EMIVerified } = useWalletContext();
  const MenuList = getMenuList(I18n);
  const MenuBottomList = getMenuBottomList(I18n);

  const tempUserId = Cookies.get("tempUserId");

  const { business, user, handleLogoutInDB } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarOpen = isPinned || isHovered;
  const hoverTimeoutRef = useRef(null);
  const hoverAreaRef = useRef(null);
  const isDarkTheme = theme === "dark";
  const sidebarPaperBg = isDarkTheme ? "var(--theme-dark-bg)" : "#fff";
  const sidebarPaperColor = isDarkTheme ? "#F8FAFC" : "#ffffff";

  useEffect(() => {
    handleSelectedRoute(location);
  }, [location]);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!disableHoverClose && !isPinned) {
      const isMouseOver = hoverAreaRef.current?.matches(":hover");
      if (!isMouseOver) {
        onHoverChange?.(false);
      }
    }
  }, [disableHoverClose]);

  const handleSelectedRoute = (currentLocation) => {
    const pathname = currentLocation?.pathname || "";
    // Remove leading slash and use the full path for matching
    const route = pathname.startsWith("/") ? pathname.substring(1) : pathname;
    setCurrentSelection(route);
    // Find the best matching route from all menu items
    if (!route) {
      setBestMatchingRoute("");
      return;
    }
    // Combine all menu items from both MenuList and MenuBottomList
    const allMenuItems = [...MenuList, ...MenuBottomList];

    for (const item of allMenuItems) {
      if (item.relatedRoutes?.includes(route)) {
        setBestMatchingRoute(item.route);
        return;
      }
    }

    const allRoutes = allMenuItems.map((item) => item.route).filter(Boolean);

    // Find the longest route that matches the current path
    // Priority: exact match > starts with route + "/"
    let bestMatch = "";
    let bestMatchLength = 0;

    for (const itemRoute of allRoutes) {
      if (route === itemRoute) {
        // Exact match is always the best
        setBestMatchingRoute(itemRoute);
        return;
      }
      // Check if current path starts with route + "/"
      if (
        route.startsWith(itemRoute + "/") &&
        itemRoute.length > bestMatchLength
      ) {
        bestMatch = itemRoute;
        bestMatchLength = itemRoute.length;
      }
    }

    setBestMatchingRoute(bestMatch || route);
  };

  const isRouteSelected = (itemRoute) => {
    return bestMatchingRoute === itemRoute;
  };

  const LANGUAGE_MODULES = { en: eng, ur, snd, romn };
  const LANGUAGE_ANALYTICS = {
    en: ["setting_language_english", { type: "English" }],
    ur: ["setting_language_urdu", { type: "Urdu" }],
    snd: ["setting_language_sindhi", { type: "Sindhi" }],
    romn: ["setting_language_roman", { type: "Roman" }],
  };

  const chooseLanguage = (lang) => {
    const languageModule = LANGUAGE_MODULES[lang];
    if (languageModule) {
      const [eventName, eventParams] = LANGUAGE_ANALYTICS[lang];
      triggerLogEvent(analytics, eventName, eventParams);
      changeLanguage(languageModule);
      localStorage.setItem("language", lang);
    }
    setLanguageModal(false);
    setLanguage(lang);
    setLangInAsyncStorage(lang);
  };

  const handleThemeChange = (newTheme) => {
    changeTheme(newTheme);
    setAppearenceModal(false);
  };

  const manageLogout = async () => {
    triggerLogEvent(analytics, "logout", {});
    let syncStatus = await hasUnsyncedChanges({ database });
    if (syncStatus && user?.phone_number) {
      setLogout(true);
      setLogoutDialogOpen(true);
      return;
    } else {
      setLogoutDialogOpen(true);
      return;
    }
  };

  const handleToggle = (type) => {
    if (type === "Backup") {
      if (!tempUserId) {
        setIsBackup(true);
      } else
        showSnackbar({
          message:
            "Please verify your number before proceeding with the backup data",
          severity: "error",
        });
    } else if (type === "Printer") setPrinterProcess(true);

    let timer = setTimeout(() => {
      setToggle(true);
    });
    return () => clearTimeout(timer);
  };

  const handleRoute = async (route, params) => {
    if (route === "Setting") return;
    else if (route === "Language") {
      triggerLogEvent(analytics, "setting_language");
      setLanguageModal(true);
    } else if (route == "Appearance") {
      triggerLogEvent(analytics, "setting_appearence");
      setAppearenceModal(true);
    } else if (route == "Logout") {
      triggerLogEvent(analytics, "setting_logout");
      manageLogout();
    } else if (route === "shopWholesale") {
      window.open("https://saddar.app");
    } else {
      if (route === "Pincode" && tempUserId) {
        showSnackbar({
          message: I18n.first_create_your_pin,
          severity: "error",
        });
        return;
      }
      if (route === "UCash" || route == "UCash/AllTransaction") {
        if (EMIVerified) {
          navigate(`/${route}`);
          return;
        }
        if (!user?.phone_number) {
          navigate("/WalletOnboarding");
          return;
        }
        setIsHomeLoading(true);
        try {
          const isVerifiedEMI = await handleCheckEMIStatus(navigate);
          navigate(isVerifiedEMI ? `/${route}` : "/WalletOnboarding");
        } catch {
          navigate("/WalletOnboarding");
        } finally {
          setIsHomeLoading(false);
        }
        return;
      }
      navigate(`/${route}`, params);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [location]);

  const handleItem = (item, hasChildren) => {
    if (hasChildren) {
      // Keyed by route so the open submenu survives a language change ("Sales" is open by default)
      setExpandedMenu((prev) => (prev === item.route ? null : item.route));
      if (item.route) {
        handleRoute(item.route);
      }
    } else if (item.value) {
      handleRoute(item.route, { state: { saleMode: item.value } });
    } else {
      handleRoute(item.route);
    }
  };

  const handleSidebarMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    onHoverChange?.(true);
  };

  const handleSidebarMouseLeave = (event) => {
    if (isPinned) return;
    if (disableHoverClose) return;
    const nextTarget = event.relatedTarget;
    if (
      nextTarget instanceof Node &&
      hoverAreaRef.current?.contains(nextTarget)
    ) {
      return;
    }
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      onHoverChange?.(false);
    }, 150);
  };

  const handleToggleLogout = (type) => {
    if (type === "Backup") {
      setIsBackup(true);
    }
    let timer = setTimeout(() => {
      setToggle(true);
    });
    return () => clearTimeout(timer);
  };

  const handleBackup = () => {
    setLogout(false);
    setLogoutDialogOpen(false);
    navigate("/Backup", {
      state: {
        isBackup: true,
      },
    });
  };

  const handleLogout = async (log = false) => {
    if (log) {
      // Trigger backup modal before logout
      handleBackup();
    } else {
      setIsLogoutLoading(true);
      await onLogout();
    }
  };

  const onLogout = async () => {
    try {
      const language = await getLangFromAsyncStorage();
      if (language) {
        setLangInAsyncStorage(language);
      }
      await handleLogoutInDB(navigate);
      setIsLogoutLoading(false);
      setLogoutDialogOpen(false);
      navigate("/", { replace: true });
    } catch (error) {
      setIsLogoutLoading(false);
      setLogoutDialogOpen(false);
    }
  };

  const handleLogoutClose = () => {
    if (isLogout) {
      setLogout(false);
    }
    setLogoutDialogOpen(false);
  };

  return (
    <Box className={styles.root}>
      <BusinessLoader loadBusiness={isHomeLoading} />
      <Drawer
        variant="permanent"
        PaperProps={{
          ref: hoverAreaRef,
          onMouseEnter: handleSidebarMouseEnter,
          onMouseLeave: handleSidebarMouseLeave,
          sx: {
            width: sidebarOpen ? DEFAULT_OPEN_WIDTH : DEFAULT_CLOSED_WIDTH,
            transition: "width 0.3s ease-in-out",
            overflowX: "hidden",
            overflowY: "hidden",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            borderRight: "none",
            boxSizing: "border-box",
            zIndex: 99,
            backgroundColor: sidebarPaperBg,
            color: sidebarPaperColor,
            top: headerOffset,
            height: `calc(100vh - ${headerOffset}px)`,
            transition: "width 0.3s ease-in-out, background-color 0.3s ease",
          },
        }}
        sx={{
          flexShrink: 0,
          zIndex: 2,
          padding: 0,
        }}
      >
        <Box
          className={`${styles.sidebarWrapper} ${sidebarOpen ? styles.expanded : styles.collapsed} ${isDarkTheme ? styles.sidebarDark : ""}`}
        >
          <Box className={styles.sidebarHeader}>
            <IconButton
              className={styles.sidebarMenuButton}
              onClick={onMenuToggle}
              aria-label="Toggle sidebar"
              size="small"
            >
              <HumburgerIcon />
            </IconButton>
            <Box className={styles.sidebarLogo}>
              {sidebarOpen ? <RupinLogo /> : null}
            </Box>
          </Box>

          <Box className={styles.sideNameItemWrapper}>
            <Box
              className={`${styles.headingContainer} ${styles.headingFirstBox}`}
            >
              <ParagraphText className={styles.navHeading}>
                {I18n.money}
              </ParagraphText>
              <ParagraphText className={styles.navHeadingBorder}>
                <span></span>
              </ParagraphText>
            </Box>
            {MenuList.map((item, index) => {
              const hasChildren = item.children && item.children.length > 0;

              const isSelected = isRouteSelected(item.route);

              return (
                <Box
                  className={`${styles.sideNameItemInnerWrapper} ${
                    isSelected ? styles.selected : ""
                  } ${hasChildren && expandedMenu === item.route ? styles.submenuExpanded : ""}`}
                  onClick={() => handleItem(item, hasChildren)}
                  key={index}
                  id={item.route}
                >
                  <Tooltip
                    title={!sidebarOpen ? item.label : ""}
                    placement="right"
                  >
                    <Box className={styles.sideNameItem}>
                      <ParagraphText className={styles.sideNavItemIcon}>
                        {item.icon}
                      </ParagraphText>
                      <ParagraphText className={styles.sideNavItemText}>
                        {item.label}
                        {hasChildren && (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              handleItem(item, hasChildren);
                            }}
                            className={
                              expandedMenu === item.route
                                ? styles.downIconExpendable
                                : styles.downIconNonExpendable
                            }
                            style={{ marginLeft: 8 }}
                          >
                            <DownIcon />
                          </span>
                        )}
                      </ParagraphText>
                    </Box>
                  </Tooltip>

                  {hasChildren &&
                    expandedMenu === item.route &&
                    sidebarOpen && (
                      <Box
                        className={styles.sideNavSubmenu}
                        onMouseEnter={() => {
                          if (hoverTimeoutRef.current) {
                            clearTimeout(hoverTimeoutRef.current);
                          }
                          onHoverChange?.(true);
                        }}
                      >
                        {item.children.map((sub, idx) => {
                          const isSubmenuSelected =
                            location.state?.saleMode === sub.value;
                          return (
                            <Box
                              id={removeAllSpaces(sub.value)}
                              className={`${styles.sideNavSubmenuItem} ${isSubmenuSelected ? styles.selected : ""}`}
                              key={idx}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (hoverTimeoutRef.current) {
                                  clearTimeout(hoverTimeoutRef.current);
                                }
                                onHoverChange?.(true);
                                handleRoute(item.route, {
                                  state: { saleMode: sub.value },
                                });
                              }}
                              onMouseEnter={() => {
                                if (hoverTimeoutRef.current) {
                                  clearTimeout(hoverTimeoutRef.current);
                                }
                                onHoverChange?.(true);
                              }}
                            >
                              {sub.label}
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                </Box>
              );
            })}
            <Fragment />
            <Box
              className={`${styles.businessSpace} ${styles.headingContainer}`}
            >
              <ParagraphText className={styles.navHeading}>
                {I18n.business}
              </ParagraphText>
              <ParagraphText className={styles.navHeadingBorder}>
                <span></span>
              </ParagraphText>
            </Box>

            {MenuBottomList.map((item, index) => {
              const hasChildren =
                (item.nested && item.nested.length > 0) ||
                (item.children && item.children.length > 0);
              // Match on route, not label: labels are translated (e.g. "سیلر سینٹرل" in Urdu)
              if (item.route === "Reports" && tempUserId) return;
              if (
                item.route === "Saddar" &&
                !localStorage.getItem("is_marketplace_seller")
              )
                return;
              const isSelected = isRouteSelected(item.route);
              return (
                <Box
                  className={`${styles.sideNameItemInnerWrapper} ${
                    isSelected ? styles.selected : ""
                  } ${hasChildren && expandedMenu === item.route ? styles.submenuExpanded : ""}`}
                  id={item.route}
                  onClick={() => handleItem(item, hasChildren)}
                  key={index}
                >
                  <Tooltip
                    title={!sidebarOpen ? item.label : ""}
                    placement="right"
                  >
                    <Box className={styles.sideNameItem}>
                      <ParagraphText className={styles.sideNavItemIcon}>
                        {item.icon}
                      </ParagraphText>
                      <ParagraphText className={styles.sideNavItemText}>
                        {item.label}
                        {hasChildren && (
                          <span
                            className={
                              expandedMenu === item.route
                                ? styles.downIconExpendable
                                : styles.downIconNonExpendable
                            }
                            style={{ marginLeft: 8 }}
                          >
                            <DownIcon />
                          </span>
                        )}
                      </ParagraphText>
                    </Box>
                  </Tooltip>

                  {hasChildren &&
                    expandedMenu === item.route &&
                    sidebarOpen && (
                      <Box
                        className={styles.sideNavSubmenu}
                        onMouseEnter={() => {
                          if (hoverTimeoutRef.current) {
                            clearTimeout(hoverTimeoutRef.current);
                          }
                          onHoverChange?.(true);
                        }}
                      >
                        {(item.nested || item.children || [])
                          .filter(
                            (sub) => !(sub.route === "Pincode" && tempUserId),
                          )
                          .map((sub, idx) => {
                            const isSubmenuSelected = sub.value
                              ? location.state?.saleMode === sub.value
                              : location.pathname.includes(sub.route);
                            return (
                              <Box
                                id={sub.route}
                                className={`${styles.sideNavSubmenuItem} ${isSubmenuSelected ? styles.selected : ""}`}
                                key={idx}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                  }
                                  onHoverChange?.(true);
                                  if (sub.value) {
                                    let routePath = item.route;

                                    if (item.route == "Sales") {
                                      const pageMap = {
                                        sale_invoice: "SaleInvoice",
                                        quick_sale: "QuickSale",
                                        sale_pos: "POS",
                                      };

                                      routePath = `Sales/${pageMap[sub.value]}`;
                                    } else if (item.route == "Setting") {
                                      routePath = sub.route;
                                    }
                                    handleRoute(routePath, {
                                      state: { saleMode: sub.value },
                                    });
                                  } else {
                                    handleRoute(sub.route);
                                  }
                                }}
                                onMouseEnter={() => {
                                  if (hoverTimeoutRef.current) {
                                    clearTimeout(hoverTimeoutRef.current);
                                  }
                                  onHoverChange?.(true);
                                }}
                              >
                                {sub.label}
                              </Box>
                            );
                          })}
                      </Box>
                    )}
                </Box>
              );
            })}
          </Box>

          <Box className={styles.sideNavBottomWrapper}></Box>
        </Box>
      </Drawer>

      <ChangeLanguageModal
        open={languageModal}
        onClose={() => setLanguageModal(false)}
        onConfirm={chooseLanguage}
        value={language}
      />

      <AppearanceModal
        open={appearenceModal}
        onClose={() => setAppearenceModal(false)}
        onConfirm={handleThemeChange}
        value={theme}
      />

      <ConfirmationModal
        id="logout_modal"
        open={logoutDialogOpen}
        onClose={!isLogoutLoading ? handleLogoutClose : () => {}}
        onConfirm={() => handleLogout(isLogout ? true : false)}
        message={
          isLogout
            ? I18n.data_not_backup
            : user?.phone_number
              ? I18n.are_you_sure_you_want_to_logout
              : I18n.are_you_sure_you_want_to_logout_all_data_will_be_lost
        }
        confirmText={isLogout ? I18n.logout_backup : I18n.log_out}
        cancelText={I18n.cancel}
        loading={!isLogout && isLogoutLoading}
        confirmButtonClassName="ubPrimaryButton"
        customClassName={styles.logoutModal}
      />
    </Box>
  );
};

export default AppSideNav;
